import Image from 'next/image';

import { Box, Card, chakra, Flex, Heading, Text } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';

export type TestimonialSectionProps = {
  title: string;
  testimonials: Array<{
    quote: string;
    name: string;
    imagePath: string;
    title: string;
  }>;
};

export function TestimonialSection({ title, testimonials }: TestimonialSectionProps) {
  return (
    <Box
      as='section'
      bgColor='#F0F5FF'
      position='relative'
      padding={{
        base: '40px 0px',
        md: '80px 0px',
      }}
    >
      <Box inset={0} position='absolute'>
        <Image
          alt=''
          src='/images/agentic-ai/testimonial-background.png'
          style={{
            objectFit: 'cover',
            objectPosition: 'center',
          }}
          fill
        />
      </Box>
      <ContentContainer
        position='relative'
        sx={{
          '&::after': {
            display: {
              base: 'none',
              md: 'block',
            },
            content: "''",
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: {
              base: '-40px',
              md: '-80px',
            },
            border: '0.5px solid #6491FC33',
          },
        }}
      >
        <Heading
          as='h2'
          textAlign='center'
          mb={{
            base: 6,
            md: 12,
          }}
          size={{
            base: 'marketing-2xl',
            md: 'marketing-5xl',
          }}
          whiteSpace={{
            base: 'pre-wrap',
            md: 'unset',
          }}
        >
          {title}
        </Heading>
        <Flex
          overflow='auto'
          gap={{
            base: 6,
            md: 10,
          }}
          marginX={{
            base: '-16px',
            md: '-48px',
            xl: 0,
          }}
          paddingX={{
            base: '16px',
            md: '48px',
            xl: 0,
          }}
        >
          {testimonials.map((testimonial) => (
            <Card
              key={testimonial.name}
              as='article'
              bgColor='#070733'
              borderRadius='12px'
              display='flex'
              flexDirection='column'
              gap={4}
              isolation='isolate'
              overflow='hidden'
              padding='0px 20px 20px'
              position='relative'
              flex={{
                base: '1 0 288px',
                md: '1 0 346.667px',
              }}
              height={{
                base: '380px',
                md: '500px',
              }}
              sx={{
                '&::before': {
                  content: '""',
                  display: 'block',
                  flex: {
                    base: '1 0 176px',
                    md: '0 0 323px',
                  },
                },
              }}
            >
              <Box
                bottom='64px'
                left={0}
                position='absolute'
                right={0}
                top={0}
                sx={{
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '80px',
                    background: 'linear-gradient(to top, #070733, transparent)',
                    pointerEvents: 'none',
                  },
                }}
              >
                <Image
                  alt=''
                  loading='lazy'
                  quality={75}
                  sizes='346px'
                  src={testimonial.imagePath}
                  style={{
                    objectFit: 'cover',
                    objectPosition: 'top',
                    pointerEvents: 'none',
                  }}
                  fill
                />
              </Box>
              <chakra.blockquote
                color='white'
                display='flex'
                flex='1'
                flexDirection='column'
                gap={2}
                position='relative'
              >
                <Heading
                  as='p'
                  fontFamily='body'
                  fontWeight='400'
                  fontSize={{
                    base: '0.8125rem',
                    md: '0.9375rem',
                  }}
                  lineHeight={{
                    base: '1.375rem',
                    md: '1.5rem',
                  }}
                  sx={{
                    '&::after': {
                      content: '"”"',
                    },
                    '&::before': {
                      content: '"“"',
                    },
                  }}
                >
                  {testimonial.quote}
                </Heading>
                <chakra.footer mt='auto'>
                  <Text
                    background='linear-gradient(90.09deg, #62BBFF 25.17%, #006AFF 52.01%)'
                    backgroundClip='text'
                    fontSize='0.875rem'
                    fontWeight='700'
                    lineHeight='1.4375rem'
                    textShadow='0 0 24px rgba(98, 187, 255, 0.5)'
                    sx={{
                      backgroundClip: 'text',
                      textFillColor: 'transparent',
                    }}
                  >
                    {testimonial.name}
                  </Text>
                  <Text fontSize='0.75rem' fontWeight='400' lineHeight='1.0625rem'>
                    {testimonial.title}
                  </Text>
                </chakra.footer>
              </chakra.blockquote>
            </Card>
          ))}
        </Flex>
      </ContentContainer>
    </Box>
  );
}
